// File Metadata:
// Filename: PWAInstallPrompt.jsx
// Version: 1.00
// Created: 2025-05-03
// Last Modified: 2025-05-03
// Authors: GitHub Copilot
// Description: Component for handling PWA installation prompt

import { useState, useEffect } from 'react';
import { Button, Snackbar, Alert, IconButton } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';

const PWAInstallPrompt = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isFirefox, setIsFirefox] = useState(false);
  
  // Check if already installed as PWA
  useEffect(() => {
    const checkInstalled = () => {
      // Check if running as standalone PWA
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                          window.navigator.standalone === true;
      
      // Check if Firefox
      const firefoxCheck = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
      setIsFirefox(firefoxCheck);
      
      if (isStandalone) {
        setIsInstalled(true);
        return;
      }
      
      // Do not show a forced install prompt for Firefox — it doesn't support
      // the `beforeinstallprompt` flow on desktop and manual instructions
      // are not needed as a persistent bar. Browsers that support the
      // install event will trigger `beforeinstallprompt` and show UI.
    };

    checkInstalled();

    // Listen for the beforeinstallprompt event (Chrome/Edge/Safari)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault(); // Prevent the default browser install prompt
      setInstallPrompt(e); // Store the event for later use
      setShowPrompt(true); // Show our custom install prompt
    };

    // Listen for successful installation
    const handleAppInstalled = () => {
      console.log('PWA was installed');
      setIsInstalled(true);
      setShowPrompt(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      // Clean up event listeners
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstalled]);

  const handleInstallClick = async () => {
    if (isFirefox) {
      // Firefox doesn't support beforeinstallprompt
      // Show instructions for manual installation
      alert('To install FamilySync in Firefox:\n1. Click the menu button (☰) in the top right\n2. Select "Install This Site as an App"\n3. Click "Install" in the dialog');
      setShowPrompt(false);
      return;
    }
    
    if (!installPrompt) return;
    
    // Show the installation prompt (Chrome/Edge/Safari)
    installPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const userChoice = await installPrompt.userChoice;
    
    if (userChoice.outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }
    
    // Reset the prompt variable, since prompt() can only be called once
    setInstallPrompt(null);
    setShowPrompt(false);
  };

  const handleClose = () => {
    setShowPrompt(false);
  };

  if (isInstalled || !showPrompt) return null;

  return (
    <Snackbar 
      open={showPrompt} 
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert 
        severity="info" 
        action={
          <>
            <Button 
              color="primary" 
              size="small" 
              startIcon={<DownloadIcon />}
              onClick={handleInstallClick}
            >
              {isFirefox ? 'Show Instructions' : 'Install'}
            </Button>
            <IconButton 
              size="small" 
              aria-label="close" 
              color="inherit" 
              onClick={handleClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
      >
        {isFirefox 
          ? 'Install FamilySync as an app in Firefox!' 
          : 'Install FamilySync as an app!'
        }
      </Alert>
    </Snackbar>
  );
};

export default PWAInstallPrompt;